import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import {
    Dimmer,
    Grid,
    Header,
    Image,
    Loader,
    Segment,
} from "semantic-ui-react";
import DynamicNotationContainer from "../../components/notation/DynamicNotationContainer";
import { LoadingState } from "../../services/http";
import Instruments from "../../assets/notation/instruments.json";
import TwinkleTwinkle from "../../assets/notation/twinkle-twinkle.json";
import Greensleeves from "../../assets/notation/greensleeves.json";
import StarSpangledBanner from "../../assets/notation/starspangledbanner.json";
import Tetris from "../../assets/notation/tetris.json";
import FurElise from "../../assets/notation/fur-elise.json";
import TheSwan from "../../assets/notation/theswan.json";
import HeyJude from "../../assets/notation/heyjude.json";
import LoadingText from "../../assets/loading_text.png";
import howToPlay from "../../assets/howtoplay-compressor.png";
import { Event } from "../../services/Event";
import "./DemoPlayer.css";

const SONGS = [
    { name: "Twinkle Twinkle", data: TwinkleTwinkle },
    { name: "Greensleeves", data: Greensleeves },
    { name: "Star Spangled Banner", data: StarSpangledBanner },
    { name: "Tetris Theme", data: Tetris },
    { name: "Fur Elise", data: FurElise },
    { name: "The Swan", data: TheSwan },
    { name: "Hey Jude", data: HeyJude },
];

const DemoPlayer = ({ bus }) => {
    const [loadingState, setLoadingState] = useState(LoadingState.PENDING);
    const [notation, setNotation] = useState(undefined);
    const [instruments, setInstruments] = useState(undefined);
    const [active, setActive] = useState(true);
    const [currentSongIndex, setCurrentSongIndex] = useState(0);
    const [jiggleLeft, setJiggleLeft] = useState(false);
    const [jiggleRight, setJiggleRight] = useState(false);
    const [slideDirection, setSlideDirection] = useState("next");

    useEffect(() => {
        loadNotation(SONGS[0].data);
    }, []);

    const loadNotation = (piece) => {
        setInstruments(Instruments);
        setNotation(piece);
        setLoadingState(LoadingState.SUCCESS);
    };

    const handleHide = (bus) => {
        bus.send(Event.EnablePlayer, {});
        setActive(false);
    };

    const handlePreviousSong = () => {
        if (currentSongIndex === 0) {
            // Jiggle animation at boundary
            setJiggleLeft(true);
            setTimeout(() => setJiggleLeft(false), 500);
        } else {
            // Go to previous song and reset to page 1
            const newIndex = currentSongIndex - 1;
            // Update slideDirection first using flushSync, then notation
            flushSync(() => {
                setSlideDirection("prev");
            });
            setCurrentSongIndex(newIndex);
            setNotation(SONGS[newIndex].data);
        }
    };

    const handleNextSong = () => {
        if (currentSongIndex === SONGS.length - 1) {
            // Jiggle animation at boundary
            setJiggleRight(true);
            setTimeout(() => setJiggleRight(false), 500);
        } else {
            // Go to next song and reset to page 1
            const newIndex = currentSongIndex + 1;
            // Update slideDirection first using flushSync, then notation
            flushSync(() => {
                setSlideDirection("next");
            });
            setCurrentSongIndex(newIndex);
            setNotation(SONGS[newIndex].data);
        }
    };

    if (loadingState === LoadingState.PENDING) {
        return <LoadingNotation />;
    } else if (loadingState === LoadingState.FAILED) {
        return null;
    } else {
        return (
            <div className="demoplayer">
                <Dimmer.Dimmable dimmed={active} className={"howtoplay"}>
                    <Dimmer
                        onClick={() => handleHide(bus)}
                        inverted
                        active={active}
                    >
                        <Header
                            style={{
                                cursor: "pointer",
                                marginTop: "-180px",
                            }}
                            as="h2"
                        >
                            <div className="instruction">
                                <p>Use your Qwerty keys to play music</p>
                                <p className="continue">click to continue</p>
                                <Image
                                    alt="Fingers and keyboard demonstrating how to play musitude"
                                    style={{ width: "20em" }}
                                    src={howToPlay}
                                />
                            </div>
                        </Header>
                    </Dimmer>

                    <DynamicNotationContainer
                        demoplayer={true}
                        bus={bus}
                        notation={notation}
                        instruments={instruments}
                        currentSongIndex={currentSongIndex}
                        songsCount={SONGS.length}
                        jiggleLeft={jiggleLeft}
                        jiggleRight={jiggleRight}
                        onPreviousSong={handlePreviousSong}
                        onNextSong={handleNextSong}
                        slideDirection={slideDirection}
                    />
                </Dimmer.Dimmable>
            </div>
        );
    }
};

const LoadingNotation = () => {
    return (
        <Grid padded={true} columns={1} style={{ height: "100%" }}>
            <Grid.Column width={16}>
                <Segment padded="very">
                    <Dimmer active inverted>
                        <Loader size="large">Rendering Notation</Loader>
                    </Dimmer>
                    <Image src={LoadingText} />
                </Segment>
            </Grid.Column>
        </Grid>
    );
};

export default DemoPlayer;
